import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { DashboardContext } from './context';
import { CoKitchenType } from 'models/cokitchen.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { FormVendorType } from 'models/vendors.model';
import { StoreItemType } from 'models/store-item.model';
import { RoleType } from 'models/role.model';
import { StoreType } from 'models/store.model';
import { useNavigate } from 'react-router-dom';
import { MAIN_STORE_ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
const DashboardProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [activeCokitchen, setActiveCokitchen] = useState({} as CoKitchenType);
  const [cokitchens, setCokitchens] = useState<CoKitchenType[]>([]);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isBackLink, setIsBackLink] = useState(false);
  const [backLinkTitle, setBackLinkTitle] = useState('');
  const [hideSideBar, setHideSideBar] = useState(window.innerWidth <= 1024);
  const [backLink, setBackLink] = useState('');
  const [formVendorsList, setFormVendorsList] = useState<FormVendorType[]>([]);
  const [isFetchingFormVendorsList, setIsFetchingFormVendorsList] = useState(false);
  const [formIngredientCategoryList, setFormIngredientCategoryList] = useState<string[]>([]);
  const [isFetchingFormIngredientCategoryList, setIsFetchingFormIngredientCategoryList] =
    useState(false);
  const [formStoreItems, setFormStoreItems] = useState<StoreItemType[]>([]);
  const [isFetchingFormStoreItems, setIsFetchingFormStoreItems] = useState(false);
  const [showAddStoreModal, setShowAddStoreModal] = useState(false);
  const [roles, setRoles] = useState<RoleType[] | null>(null);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [isStoresLoading, setIStoresLoading] = useState(false);
  const [refreshStore, setRefreshStore] = useState(false);
  const navigate = useNavigate();
  const persistedStore = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.ACTIVE_STORE) ?? '{}');
  const [activeStore, setActiveStore] = useState<undefined | StoreType>(persistedStore);
  const [showStores, setShowStores] = useState(false);

  useEffect(() => {
    if (!activeStore?.id) {
      navigate(MAIN_STORE_ROUTE_KEYS.GENERAL_STORE_DASHBOARD);
    }
  }, [activeStore?.id]);

  const fetchVendors = async () => {
    try {
      setIsFetchingFormVendorsList(true);
      const response = await apiService.get(`vendors/lean`, {
        version: 'v2'
      });
      if (response) {
        setFormVendorsList([...response.data.data]);
        return response.data.results.length;
      }
    } catch (err: any) {
      console.log(err);
    } finally {
      setIsFetchingFormVendorsList(false);
    }
  };

  const fetchStoreItems = async (page: number) => {
    try {
      setIsFetchingFormStoreItems(true);
      const response = await apiService.get(`ingredients`, {
        params: {
          page,
          page_size: '50'
        }
      });
      if (response) {
        setFormStoreItems((prev) => [...prev, ...response.data.results]);
        return response.data.results.length;
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err?.response?.data?.message || 'Error fetching store items'}
          type='error'
        />
      );
    } finally {
      setIsFetchingFormStoreItems(false);
    }
  };

  const fetchAllStoreItems = useCallback(async () => {
    let page = 1;
    let resultsLength = 10;

    while (resultsLength >= 10) {
      resultsLength = await fetchStoreItems(page);
      page += 1;
    }
  }, []);

  const fetchIngredientsCategries = async () => {
    try {
      setIsFetchingFormIngredientCategoryList(true);
      const response = await apiService.get('ingredients/categories');
      if (response) {
        setFormIngredientCategoryList(
          response?.data?.map((ingredient: { category: string }) => ingredient.category)
        );
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching ingredients categories'}
          type='error'
        />
      );
    } finally {
      setIsFetchingFormIngredientCategoryList(false);
    }
  };

  const getCokitchens = async () => {
    try {
      const response = await apiService.get('cokitchens', {
        baseURL: process.env.REACT_APP_BRAND_MGT_URL
      });
      if (response) {
        const data = response?.data?.data;
        setCokitchens(data);
        setActiveCokitchen(data[0]);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching cokitchens'}
          type='error'
        />
      );
    }
  };

  const getStores = async () => {
    try {
      setIStoresLoading(true);
      const res = await apiService.get(`stores/cokitchen/${activeCokitchen.id}/stores`);

      setStores(res.data);
    } catch (e) {
      console.log(e);
    } finally {
      setIStoresLoading(false);
    }
  };

  useEffect(() => {
    getStores();
  }, [activeCokitchen, refreshStore]);

  useEffect(() => {
    fetchVendors();
    fetchIngredientsCategries();
    fetchAllStoreItems();
    getCokitchens();
  }, []);

  return (
    <DashboardContext.Provider
      value={{
        activeCokitchen,
        setActiveCokitchen,
        cokitchens,
        setCokitchens,
        showMobileSidebar,
        setShowMobileSidebar,
        isBackLink,
        setIsBackLink,
        backLinkTitle,
        setBackLinkTitle,
        hideSideBar,
        setHideSideBar,
        backLink,
        setBackLink,
        formVendorsList,
        isFetchingFormVendorsList,
        isFetchingFormIngredientCategoryList,
        formIngredientCategoryList,
        formStoreItems,
        isFetchingFormStoreItems,
        activeStore,
        setActiveStore,
        showAddStoreModal,
        setShowAddStoreModal,
        roles,
        setRoles,
        stores,
        setStores,
        isStoresLoading,
        setIStoresLoading,
        refreshStore,
        setRefreshStore,
        setShowStores,
        showStores
      }}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;
